/**
 * Poker Hub - Choose your poker variant
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Spade, Users, Globe, GraduationCap, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BOOSTY_LOGO_URL } from './pokerConstants.js';

const PokerHub = () => {
  const navigate = useNavigate();
  
  const variants = [
    {
      id: 'holdem',
      name: 'Texas Hold\'em',
      description: 'The world\'s most popular poker game',
      details: '2 hole cards + 5 community cards',
      path: '/games/poker/texas-holdem',
      color: 'red',
      icon: '🃏',
      badge: 'POPULAR'
    },
    {
      id: 'three-card',
      name: 'Three Card Poker',
      description: 'Fast-paced 3-card action',
      details: '3 cards, one betting round',
      path: '/games/poker/three-card-stud',
      color: 'amber',
      icon: '🎰',
      badge: null
    }
  ];
  
  const colorStyles = {
    red: { bg: 'bg-red-600/20', border: 'border-red-500/30', hover: 'hover:border-red-500/60', text: 'text-red-400' },
    amber: { bg: 'bg-amber-600/20', border: 'border-amber-500/30', hover: 'hover:border-amber-500/60', text: 'text-amber-400' }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/30 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto p-6">
        {/* Title Section */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <img src={BOOSTY_LOGO_URL} alt="Boosty" className="h-16 w-auto" />
          </motion.div>
          <h1 className="text-4xl font-bold text-white mb-2">Poker Room</h1>
          <p className="text-slate-400">Choose your game</p>
        </div>
        
        {/* Multiplayer Lobby Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/games/poker/lobby')}
          className="w-full mb-6 bg-gradient-to-r from-emerald-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl border border-emerald-500/40 hover:border-emerald-500/70 p-5 text-left transition-all group"
          data-testid="multiplayer-lobby-btn"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-emerald-600/30 flex items-center justify-center">
                <Globe className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-white">Multiplayer Lobby</h2>
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-500 text-white rounded-full">LIVE</span>
                </div>
                <p className="text-sm text-slate-400">Join or create tables to play with others</p>
              </div>
            </div>
            <div className="text-emerald-400 font-semibold group-hover:translate-x-1 transition-transform">
              Enter →
            </div>
          </div>
        </motion.button>
        
        {/* Practice Mode Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/games/poker/practice')}
          className="w-full mb-6 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/40 hover:border-purple-500/70 p-5 text-left transition-all group"
          data-testid="practice-mode-btn"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-purple-600/30 flex items-center justify-center">
                <Target className="w-7 h-7 text-purple-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-white">Practice Mode</h2>
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-purple-500 text-white rounded-full">LEARN</span>
                </div>
                <p className="text-sm text-slate-400">Quiz yourself on hand strength & betting</p>
              </div>
            </div>
            <div className="text-purple-400 font-semibold group-hover:translate-x-1 transition-transform">
              Start →
            </div>
          </div>
        </motion.button>
        
        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-slate-700/50" />
          <span className="text-xs text-slate-500 uppercase tracking-wider">or play solo</span>
          <div className="flex-1 h-px bg-slate-700/50" />
        </div>
        
        {/* Game Variants */}
        <div className="grid md:grid-cols-2 gap-6">
          {variants.map((variant, idx) => {
            const colors = colorStyles[variant.color];
            return (
              <motion.button
                key={variant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(variant.path)}
                className={`relative bg-slate-900/90 backdrop-blur-sm rounded-2xl border ${colors.border} ${colors.hover} p-6 text-left transition-all group`}
              >
                {variant.badge && (
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                    {variant.badge}
                  </span>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 rounded-xl ${colors.bg} flex items-center justify-center text-3xl`}>
                    {variant.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white mb-1">{variant.name}</h2>
                    <p className="text-sm text-slate-400 mb-2">{variant.description}</p>
                    <p className={`text-xs ${colors.text}`}>{variant.details}</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Users className="w-3 h-3" />
                    <span>vs AI or Multiplayer</span>
                  </div>
                  <span className={`text-sm font-semibold ${colors.text} group-hover:translate-x-1 transition-transform`}>
                    Play →
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
        
        {/* Quick Rules */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-10 bg-slate-900/50 rounded-xl border border-slate-700/50 p-6"
        >
          <h3 className="text-lg font-bold text-white mb-4">Hand Rankings (Best to Worst)</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <div className="flex justify-between text-slate-300"><span>1. Royal Flush</span><span className="text-amber-400">A K Q J 10 same suit</span></div>
              <div className="flex justify-between text-slate-300"><span>2. Straight Flush</span><span className="text-amber-400">5 consecutive same suit</span></div>
              <div className="flex justify-between text-slate-300"><span>3. Four of a Kind</span><span className="text-amber-400">4 same rank</span></div>
              <div className="flex justify-between text-slate-300"><span>4. Full House</span><span className="text-amber-400">3 of a kind + pair</span></div>
              <div className="flex justify-between text-slate-300"><span>5. Flush</span><span className="text-amber-400">5 same suit</span></div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-slate-300"><span>6. Straight</span><span className="text-amber-400">5 consecutive</span></div>
              <div className="flex justify-between text-slate-300"><span>7. Three of a Kind</span><span className="text-amber-400">3 same rank</span></div>
              <div className="flex justify-between text-slate-300"><span>8. Two Pair</span><span className="text-amber-400">2 different pairs</span></div>
              <div className="flex justify-between text-slate-300"><span>9. Pair</span><span className="text-amber-400">2 same rank</span></div>
              <div className="flex justify-between text-slate-300"><span>10. High Card</span><span className="text-amber-400">Highest card wins</span></div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PokerHub;
